<script>
// The resource layer is lazy-required in methods: importing it at module load pulls
// in router -> store -> resources/user, a circular dependency (see resources/user.js).
import messages from '@/mixins/messages'

export default {
    name: 'ReservationRacersForm',
    mixins: [messages],
    props: {
        reservationIds: {
            type: Array,
            required: true,
        },
        purchaser: {
            type: Object,
            default: null,
        },
        existingGroup: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            loading: true,
            submitting: false,
            done: false,
            error: '',
            seats: [],
            rows: [],
            pickingRowIndex: null,
        }
    },
    computed: {
        rowStates() {
            return this.rows.map((row) => {
                const filledCount = [row.email.trim(), row.first_name.trim(), row.last_name.trim()].filter(Boolean).length
                return {
                    empty: filledCount === 0,
                    complete: filledCount === 3,
                    partial: filledCount > 0 && filledCount < 3,
                }
            })
        },
        hasPartialRow() {
            return this.rowStates.some((state) => state.partial)
        },
        filledCount() {
            return this.rowStates.filter((state) => state.complete).length
        },
        pickerSeats() {
            if (this.pickingRowIndex === null) {
                return []
            }
            return this.seatOptionsForRow(this.pickingRowIndex)
        },
    },
    methods: {
        seatLabel(reservationId) {
            const seat = this.seats.find((entry) => entry.reservation === reservationId)
            return seat ? seat.calendar_name : ''
        },
        seatOptionsForRow(rowIndex) {
            const takenElsewhere = this.rows.map((row, i) => (i === rowIndex ? null : row.reservation)).filter(Boolean)
            return this.seats.filter((seat) => !takenElsewhere.includes(seat.reservation))
        },
        openSimPicker(index) {
            this.pickingRowIndex = index
            this.$refs.simPicker.show()
        },
        chooseSim(reservationId) {
            if (this.pickingRowIndex !== null) {
                this.$set(this.rows[this.pickingRowIndex], 'reservation', reservationId)
            }
            this.$refs.simPicker.hide()
        },
        clearSim(index) {
            this.$set(this.rows[index], 'reservation', null)
        },
        async load() {
            this.loading = true
            const ReservationResource = require('@/resources/reservation').default
            const reservations = await Promise.all(this.reservationIds.map((id) => ReservationResource.detail(id)))
            this.seats = this.reservationIds.map((id, i) => ({
                reservation: id,
                calendar_name: reservations[i]?.attributes.readonly_values?.calendar_name || reservations[i]?.attributes.calendar_name || 'Simulator',
            }))

            const rows = []
            if (this.purchaser) {
                rows.push({
                    email: this.purchaser.attributes.email || '',
                    first_name: this.purchaser.attributes.first_name || '',
                    last_name: this.purchaser.attributes.last_name || '',
                    reservation: null,
                    is_purchaser: true,
                })
            }
            while (rows.length < this.seats.length) {
                rows.push({ email: '', first_name: '', last_name: '', reservation: null, is_purchaser: false })
            }
            this.rows = rows
            this.loading = false
        },
        async run() {
            if (this.done) {
                return true
            }
            if (this.hasPartialRow) {
                this.error = 'Each racer needs an email, first name and last name. Fill all three or leave the row blank.'
                return false
            }
            if (this.filledCount === 0) {
                return true
            }
            this.error = ''
            this.submitting = true
            try {
                const racers = this.rows
                    .filter((row, i) => this.rowStates[i].complete)
                    .map((row) => ({
                        email: row.email.trim(),
                        first_name: row.first_name.trim(),
                        last_name: row.last_name.trim(),
                        reservation: row.reservation || null,
                    }))

                if (this.existingGroup) {
                    await this.existingGroup.addRacers(racers)
                } else {
                    const ReservationSeatingGroupResource = require('@/resources/reservationSeatingGroup').default
                    await ReservationSeatingGroupResource.createGroup({
                        source: 'CHECKOUT',
                        reservations: this.reservationIds,
                        racers,
                    })
                }
                this.done = true
                this.$emit('done', this.filledCount)
                return true
            } catch (e) {
                this.error = this.getResponseMessageFromAxiosException(e) || 'Something went wrong adding your racers.'
                return false
            } finally {
                this.submitting = false
            }
        },
    },
    async mounted() {
        await this.load()
    },
}
</script>

<template>
    <div class="reservation-racers-form">
        <div v-if="loading" class="text-center text-muted py-3"><b-spinner small /> Loading your simulators...</div>

        <div v-else-if="done">
            <b-alert variant="success" show class="mb-0">
                <b-icon icon="check-circle-fill" />
                <span v-if="filledCount > 0"> Invites are on their way to {{ filledCount }} {{ filledCount === 1 ? 'racer' : 'racers' }}.</span>
                <span v-else> Saved.</span>
            </b-alert>
        </div>

        <div v-else>
            <p class="small text-muted mb-3">Racing with friends? Add them and we'll email each of them to finish their profile and pick a simulator. This is optional.</p>

            <div v-for="(row, i) in rows" :key="i" class="racer-row rounded border p-2 mb-2" :class="{ 'border-warning': rowStates[i].partial }">
                <b-form-input v-model="row.email" type="email" size="sm" class="mb-2" :placeholder="row.is_purchaser ? 'Your email' : 'racer@email.com'" />
                <b-row no-gutters>
                    <b-col cols="6" class="pr-1"><b-form-input v-model="row.first_name" size="sm" placeholder="First name" /></b-col>
                    <b-col cols="6" class="pl-1"><b-form-input v-model="row.last_name" size="sm" placeholder="Last name" /></b-col>
                </b-row>
                <div class="mt-2">
                    <a href="javascript:{}" @click="openSimPicker(i)">
                        <b-icon icon="controller" />
                        <span v-if="row.reservation"> {{ seatLabel(row.reservation) }}</span>
                        <span v-else> Choose a simulator (optional)</span>
                    </a>
                    <a v-if="row.reservation" href="javascript:{}" class="small text-muted ml-2" @click="clearSim(i)">clear</a>
                </div>
            </div>

            <b-alert variant="warning" :show="hasPartialRow" class="small py-2">Each racer needs an email, first and last name -- fill all three or leave the row blank.</b-alert>
            <b-alert variant="danger" :show="!!error" class="small py-2">{{ error }}</b-alert>
        </div>

        <b-modal ref="simPicker" title="Choose a simulator" centered hide-footer no-fade no-enforce-focus static lazy>
            <b-list-group flush>
                <b-list-group-item button @click="chooseSim(null)">Let them choose</b-list-group-item>
                <b-list-group-item v-for="seat in pickerSeats" :key="seat.reservation" button @click="chooseSim(seat.reservation)">
                    {{ seat.calendar_name }}
                </b-list-group-item>
            </b-list-group>
        </b-modal>
    </div>
</template>
